/* SIDIZ background videos preload near the viewport and play only inside it.
   Markup: <video data-sidiz-video preload="none" poster="…">
             <source data-src="…" type="video/mp4">
           </video>
   data-eager starts an eligible visible hero before observer delivery. */

(function () {
  'use strict';

  if (window.sidizLazyVideoReady) return;
  window.sidizLazyVideoReady = true;

  var videos = new Map();
  var motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var connection = navigator.connection;
  var preloadObserver;
  var playbackObserver;

  function isVisible(video) {
    var style = window.getComputedStyle(video);
    if (video.checkVisibility && !video.checkVisibility({ opacityProperty: true, visibilityProperty: true })) return false;
    return video.isConnected && video.getClientRects().length > 0 &&
      style.display !== 'none' && style.visibility !== 'hidden' && style.visibility !== 'collapse' && style.opacity !== '0';
  }

  function inBounds(video, margin) {
    var rect = video.getBoundingClientRect();
    return rect.bottom > -margin && rect.top < window.innerHeight + margin &&
      rect.right > 0 && rect.left < window.innerWidth;
  }

  function eligible(video) {
    return !document.hidden && !motion.matches && !(connection && connection.saveData) && isVisible(video);
  }

  function applyPoster(video) {
    if (video.dataset.responsivePoster === 'true') return;
    var mobile = window.matchMedia('(max-width: 833px)').matches;
    var poster = mobile ? video.dataset.posterMobile : video.dataset.posterDesktop;
    if (!poster) poster = video.dataset.posterDesktop || video.dataset.posterMobile;
    if (poster && video.getAttribute('poster') !== poster) video.setAttribute('poster', poster);
  }

  function showPoster(video) {
    var frame = video.closest('.sidiz-video-frame');
    if (frame) frame.removeAttribute('data-playing');
  }

  function pause(video) {
    video.pause();
    showPoster(video);
  }

  function attach(video) {
    var sources = Array.prototype.slice.call(video.querySelectorAll('source[data-src]'));
    var selected = sources.filter(function (source) {
      var media = source.getAttribute('media');
      return !media || window.matchMedia(media).matches;
    });
    var key = selected.map(function (source) { return source.dataset.src; }).join('|');
    if (video.dataset.sidizSourceKey === key) return !!key;
    if (!key && !video.dataset.sidizSourceKey) return false;
    pause(video);
    sources.forEach(function (source) {
      if (selected.indexOf(source) !== -1) source.src = source.dataset.src;
      else source.removeAttribute('src');
    });
    video.dataset.sidizLoaded = key ? 'true' : 'false';
    video.dataset.sidizSourceKey = key;
    video.preload = key ? 'auto' : 'none';
    video.load();
    return !!key;
  }

  function canPlay(video, state) {
    return state.inViewport && inBounds(video, 0) && eligible(video);
  }

  function sync(video, state) {
    applyPoster(video);
    if (!eligible(video) || !(state.near || state.inViewport) || !attach(video)) {
      pause(video);
      return;
    }
    if (!canPlay(video, state)) {
      pause(video);
      return;
    }
    if (!video.paused) return;
    var attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch(function () {
        if (video.paused) showPoster(video);
      });
    }
  }

  function observe(entries, property) {
    entries.forEach(function (entry) {
      var state = videos.get(entry.target);
      if (!state) return;
      state[property] = entry.isIntersecting && (property === 'near' || entry.intersectionRatio > 0);
      sync(entry.target, state);
    });
  }

  if ('IntersectionObserver' in window) {
    preloadObserver = new IntersectionObserver(function (entries) {
      observe(entries, 'near');
    }, { rootMargin: '400px 0px', threshold: 0 });
    playbackObserver = new IntersectionObserver(function (entries) {
      observe(entries, 'inViewport');
    }, { rootMargin: '0px', threshold: 0.01 });
  }

  function measure(video, state) {
    state.near = inBounds(video, 400);
    state.inViewport = inBounds(video, 0);
  }

  function init(video) {
    if (videos.has(video)) return;
    var state = { near: false, inViewport: false };
    videos.set(video, state);
    video.dataset.sidizVideoReady = 'true';
    video.autoplay = false;
    video.removeAttribute('autoplay');
    applyPoster(video);
    state.playing = function () {
      if (!canPlay(video, state)) {
        pause(video);
        return;
      }
      var frame = video.closest('.sidiz-video-frame');
      if (frame) frame.setAttribute('data-playing', '');
    };
    state.poster = function () { showPoster(video); };
    video.addEventListener('playing', state.playing);
    video.addEventListener('pause', state.poster);
    video.addEventListener('error', state.poster);
    if (preloadObserver) {
      preloadObserver.observe(video);
      playbackObserver.observe(video);
    }
    if (!preloadObserver || video.dataset.eager === 'true') {
      measure(video, state);
      sync(video, state);
    }
  }

  function boot(scope) {
    (scope || document).querySelectorAll('video[data-sidiz-video]').forEach(init);
  }

  function destroy(video, state) {
    pause(video);
    if (preloadObserver) {
      preloadObserver.unobserve(video);
      playbackObserver.unobserve(video);
    }
    video.removeEventListener('playing', state.playing);
    video.removeEventListener('pause', state.poster);
    video.removeEventListener('error', state.poster);
    delete video.dataset.sidizVideoReady;
    videos.delete(video);
  }

  function refresh() {
    videos.forEach(function (state, video) {
      if (!video.isConnected) {
        destroy(video, state);
        return;
      }
      if (!preloadObserver) measure(video, state);
      sync(video, state);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) { boot(event.target); });
  document.addEventListener('shopify:section:unload', function (event) {
    videos.forEach(function (state, video) {
      if (event.target.contains(video)) destroy(video, state);
    });
  });
  document.addEventListener('visibilitychange', refresh);
  document.addEventListener('transitionend', refresh);
  if (motion.addEventListener) motion.addEventListener('change', refresh);
  else if (motion.addListener) motion.addListener(refresh);
  if (connection && connection.addEventListener) connection.addEventListener('change', refresh);

  var refreshTimer;
  function scheduleRefresh() {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(refresh, 120);
  }
  window.addEventListener('resize', scheduleRefresh);
  if (!preloadObserver) window.addEventListener('scroll', scheduleRefresh, { passive: true });
})();
